// Videos — script library → render → campaign-level Meta publishing.
//
// Three sections on a single page:
//   1. Selectors: brand · funnel · voice profile (compact strip)
//   2. Scripts: editorial cards with bulk-render bar
//   3. Rendered videos: thumbnail grid with per-ad Meta lifecycle state
//
// Funnels missing offer/projectType meta are nudged to the Funnels
// screen with an inline notice; we don't duplicate the editor here.

import Beacon from '../components/Beacon.jsx';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { PALETTE } from '../palette.js';
import { Card, Pill } from '../components/primitives.jsx';
import ProtectedPageLoader from '../components/ProtectedPageLoader.jsx';
import ProtectedVideo from '../components/ProtectedVideo.jsx';
import { api, getToken } from '../api.js';

const OFFER_LABELS = {
  'quiz-qualifier': 'Quiz Qualifier',
  'financed-offer': 'Financed Offer',
  'paid-design-session': 'Paid Design Session',
};
const PROJECT_LABELS = { kitchen: 'Kitchen', addition: 'Addition' };

function activeCompanyId(session) {
  const memberships = session?.user?.memberships || [];
  return session?.user?.activeCompany?.id || memberships[0]?.companyId || '';
}

function tenantHeaders(companyId) {
  return companyId ? { headers: { 'x-company-id': companyId } } : undefined;
}

function activeTenantRole(session, companyId = activeCompanyId(session)) {
  return session?.user?.role
    || session?.user?.memberships?.find((membership) => membership.companyId === companyId)?.role
    || null;
}

export function canRenderSaasVideos(session, companyId = activeCompanyId(session)) {
  return ['owner', 'admin', 'marketer'].includes(String(activeTenantRole(session, companyId) || '').toLowerCase());
}

export function canPublishSaasVideos(session, companyId = activeCompanyId(session)) {
  return ['owner', 'admin'].includes(String(activeTenantRole(session, companyId) || '').toLowerCase());
}

export function safeVideoError(error, fallback = 'Videos could not be loaded. Try again.') {
  const code = String(error?.code || '').toLowerCase();
  if (code === 'video_render_in_progress') return 'A video render is already running for this campaign.';
  if (code === 'video_render_previously_failed') return 'That render attempt failed. Start it again to create a new attempt.';
  if (code === 'video_funnel_not_deployed') return 'This historical campaign needs a hosted destination. Custom lead-site hosting is not available in the current product.';
  if (code === 'workflow_approval_required') return 'Approve this campaign before publishing its videos.';
  if (code === 'publish_approval_required') return 'Approve this campaign before publishing its videos.';
  if (code === 'funnel_version_reference_missing') return 'This historical campaign has no preserved destination version. Custom lead-site hosting is not available in the current product.';
  if (code === 'funnel_unpublished') return 'This historical campaign has no hosted destination. Custom lead-site hosting is not available in the current product.';
  if (code === 'funnel_publication_disabled') return 'This historical campaign destination is unavailable. Custom lead-site hosting is not available in the current product.';
  if (code === 'funnel_publication_stale') return 'This historical campaign destination is stale. Custom lead-site hosting is not available in the current product.';
  if (code === 'funnel_active_domain_unavailable') return 'This historical campaign has no active destination. Custom lead-site hosting is not available in the current product.';
  if (code === 'funnel_environment_unavailable') return 'Video publishing is unavailable in this environment. Contact support.';
  if (code === 'active_funnel_version_not_approved') return 'This historical campaign destination is not approved. Custom lead-site hosting is not available in the current product.';
  if (code === 'funnel_publish_destination_stale') return 'This historical campaign destination changed. Custom lead-site hosting is not available in the current product.';
  if (code === 'funnel_version_not_ready') return 'This historical campaign destination is incomplete. Custom lead-site hosting is not available in the current product.';
  if (code === 'meta_credential_required' || code === 'meta_credential_not_ready') return 'Connect and verify Meta Ads in Company settings before publishing.';
  if (code === 'publish_intent_already_in_progress' || code === 'funnel_publish_execution_in_progress') return 'A Meta publish is already running for this campaign. Wait for it to finish, then refresh.';
  if (code === 'publish_intent_execution_lease_lost') return 'The Meta publish was interrupted before finishing. Refresh and try again.';
  if (code === 'publish_selection_not_in_approved_batch') return 'This video is not part of the approved batch. Re-approve the campaign to include it.';
  if (code === 'meta_publish_ads_failed') return 'Some approved ads could not be created in Meta. Check the ad errors and try again.';
  if (code === 'meta_targeting_city_unresolved') return 'Meta could not match the campaign target city. Fix the campaign targeting area, then retry.';
  if (code === 'publish_selection_invalid') return 'The publish selection was invalid. Refresh and try again.';
  if (code === 'publish_intent_manifest_mismatch') return 'Campaign publish settings changed since the last attempt. Refresh and try again.';
  if (Number(error?.status) === 403) return 'Your company role cannot perform this video action.';
  return fallback;
}

export function saasPublishStatePresentation(state) {
  const states = {
    ready: { label: 'Ready to publish', tone: 'accent' },
    publishing: { label: 'Publishing', tone: 'accent' },
    succeeded: { label: 'Batch created in Meta', tone: 'success' },
    created_paused: { label: 'Created in Meta — paused', tone: 'success' },
    active: { label: 'Active', tone: 'success' },
    partially_failed: { label: 'Partially failed', tone: 'warn' },
    failed: { label: 'Failed', tone: 'warn' },
    not_included: { label: 'Not included in approved publish', tone: 'neutral' },
    approval_required: { label: 'Approval required', tone: 'warn' },
    needs_attention: { label: 'Needs attention', tone: 'warn' },
    unavailable: { label: 'Unavailable', tone: 'neutral' },
  };
  return states[state] || states.unavailable;
}

const videosResponsiveStyles = `
  @media (max-width: 640px) {
    .videos-page { padding: 24px 16px 48px !important; }
    .videos-script-grid, .videos-gallery-grid { grid-template-columns: minmax(0, 1fr) !important; }
  }
`;

export function saasVideoListPath(brandId, campaignId = '') {
  const query = new URLSearchParams({ brandId, limit: '100', offset: '0' });
  if (campaignId) query.set('campaignId', campaignId);
  return `/api/saas/videos?${query.toString()}`;
}

export function resolveRouteFunnelId(funnels, { funnelId = '', workflowJobId = '', campaignId = '' } = {}) {
  const rows = Array.isArray(funnels) ? funnels : [];
  return rows.find((entry) => funnelId && entry.id === funnelId)?.id
    || rows.find((entry) => workflowJobId && entry.workflowJobId === workflowJobId)?.id
    || rows.find((entry) => campaignId && entry.campaign?.id === campaignId)?.id
    || '';
}

export function metaPublishExecuteRequest(workflowJobId, artifactIds) {
  // An explicit-but-empty selection must never silently widen into a
  // whole-batch publish (mirrors the server's 400 for {artifactIds: []}).
  if (artifactIds !== undefined && artifactIds !== null
    && !(Array.isArray(artifactIds) && artifactIds.length)) {
    throw new Error('publish selection is invalid');
  }
  return {
    path: `/api/saas/workflows/${encodeURIComponent(workflowJobId)}/publish-intents/meta/execute`,
    body: artifactIds ? { artifactIds } : {},
  };
}

export function publishErrorMessageFor(publishError, scope, workflowJobId) {
  if (!publishError || publishError.scope !== scope) return '';
  if (workflowJobId && publishError.workflowJobId !== workflowJobId) return '';
  return publishError.message || '';
}

export function funnelOptionLabel(funnel = {}) {
  return funnel.name
    || funnel.title
    || funnel.campaign?.name
    || funnel.campaignName
    || funnel.slug
    || funnel.funnelSlug;
}

export default function Videos({ navigate, route, session }) {
  const [brands, setBrands] = useState(null);
  const [profiles, setProfiles] = useState(null);
  const [brandSlug, setBrandSlug] = useState('');
  const [funnels, setFunnels] = useState(null);
  const [funnelSlug, setFunnelSlug] = useState('');
  const [profileId, setProfileId] = useState('');
  const [scripts, setScripts] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [videos, setVideos] = useState(null);
  const [publishBatches, setPublishBatches] = useState([]);
  const [published, setPublished] = useState({});
  const [publishingScope, setPublishingScope] = useState(null);
  const [publishError, setPublishError] = useState(null);
  const [renderState, setRenderState] = useState({ active: false, current: '', label: '', queueIndex: 0, queueTotal: 0 });
  const [error, setError] = useState('');
  const companyId = activeCompanyId(session);
  const isSaasSession = session?.user?.auth?.type === 'supabase';
  const canRender = !isSaasSession || canRenderSaasVideos(session, companyId);
  const canPublish = !isSaasSession || canPublishSaasVideos(session, companyId);
  const routeBrandId = isSaasSession ? String(route?.query?.brand || '') : '';
  const routeFunnelId = isSaasSession ? String(route?.query?.funnel || '') : '';
  const routeCampaignId = isSaasSession ? String(route?.query?.campaign || '') : '';
  const routeWorkflowJobId = isSaasSession ? String(route?.query?.workflow || '') : '';
  const routeVideoId = isSaasSession ? String(route?.query?.video || '') : '';
  const routeSection = isSaasSession ? String(route?.query?.section || '') : '';

  // Brands + voice profiles on mount.
  useEffect(() => {
    let active = true;
    setBrands(null);
    setProfiles(null);
    setBrandSlug('');
    setFunnelSlug('');
    setError('');
    const options = tenantHeaders(companyId);
    const brandsRequest = isSaasSession
      ? api.get('/api/saas/brands', options).then((r) => r.brands || [])
      : api.get('/api/clients').then((r) => r.clients || []);
    const profilesRequest = isSaasSession
      ? api.get('/api/saas/video-profiles', options)
      : api.get('/api/video-profiles');
    brandsRequest.then((list) => { if (active) setBrands(list); }).catch((err) => {
      if (!active) return;
      setBrands([]);
      setError(safeVideoError(err));
    });
    profilesRequest.then((r) => {
      if (!active) return;
      const list = r.profiles || r || [];
      setProfiles(list);
      // Auto-pick the first profile so the render button isn't gated behind
      // an extra click. User can still change it.
      if (list.length > 0) {
        const firstId = list[0].id || list[0].profileId;
        if (firstId) setProfileId(firstId);
      }
    }).catch((err) => {
      if (!active) return;
      setProfiles([]);
      setError(safeVideoError(err));
    });
    return () => { active = false; };
  }, [isSaasSession, companyId]);

  useEffect(() => {
    if (!routeBrandId || !Array.isArray(brands)) return;
    if (brands.some((brand) => brand.id === routeBrandId)) setBrandSlug(routeBrandId);
  }, [routeBrandId, brands]);

  // Funnels for selected brand.
  useEffect(() => {
    let active = true;
    if (!brandSlug) { setFunnels(null); setFunnelSlug(''); return; }
    setFunnels(null);
    const request = isSaasSession
      ? api.get(`/api/saas/funnels?brandId=${encodeURIComponent(brandSlug)}&limit=100&offset=0`, tenantHeaders(companyId))
      : api.get(`/api/funnels/${encodeURIComponent(brandSlug)}`);
    request
      .then((r) => { if (active) setFunnels(r.funnels || []); })
      .catch((err) => {
        if (!active) return;
        setFunnels([]);
        setError(safeVideoError(err));
      });
    return () => { active = false; };
  }, [brandSlug, isSaasSession, companyId]);

  useEffect(() => {
    if (!Array.isArray(funnels)) return;
    const resolved = resolveRouteFunnelId(funnels, {
      funnelId: routeFunnelId,
      workflowJobId: routeWorkflowJobId,
      campaignId: routeCampaignId,
    });
    if (resolved) setFunnelSlug(resolved);
  }, [routeFunnelId, routeWorkflowJobId, routeCampaignId, funnels]);

  const funnel = useMemo(
    () => (funnels || []).find((f) => (isSaasSession ? f.id : (f.slug || f.funnelSlug)) === funnelSlug),
    [funnels, funnelSlug, isSaasSession],
  );

  // Scripts for selected funnel's offer × projectType.
  useEffect(() => {
    setScripts(null);
    setSelected(new Set());
    const offer = funnel?.campaign?.offer || funnel?.offer || funnel?._offer;
    const projectType = funnel?.campaign?.projectType || funnel?.projectType || funnel?._projectType;
    if (!offer || !projectType) return;
    const request = isSaasSession
      ? api.get(`/api/saas/video-scripts?funnelId=${encodeURIComponent(funnel.id)}`, tenantHeaders(companyId))
      : api.get(`/api/ad-scripts/${encodeURIComponent(offer)}/${encodeURIComponent(projectType)}`);
    request
      .then((r) => setScripts(r.scripts || r || []))
      .catch((err) => {
        setScripts([]);
        setError(safeVideoError(err));
      });
  }, [funnel?.id, funnel?.offer, funnel?._offer, funnel?.projectType, funnel?._projectType, funnel?.campaign?.offer, funnel?.campaign?.projectType, isSaasSession, companyId]);

  // Rendered videos for the brand.
  const refreshPublished = useCallback(() => {
    if (!brandSlug) return;
    if (isSaasSession) {
      return api.get(saasVideoListPath(brandSlug, routeCampaignId), tenantHeaders(companyId))
        .then((r) => {
          setVideos(r.videos || []);
          setPublishBatches(r.publishBatches || []);
        })
        .catch((err) => setError(safeVideoError(err)));
    }
    api.get(`/api/meta/published/${encodeURIComponent(brandSlug)}`)
      .then((r) => setPublished(r.byFilename || r || {}))
      .catch(() => setPublished({}));
    // Re-pull videos too — the sidecar gets a fresh metaVideoId/publishedAs
    // entry server-side after publish, and the card uses both.
    return api.get(`/api/videos/${encodeURIComponent(brandSlug)}`)
      .then((r) => setVideos(r.videos || r || []))
      .catch(() => {});
  }, [brandSlug, routeCampaignId, isSaasSession, companyId]);

  useEffect(() => {
    if (!brandSlug) { setVideos(null); setPublishBatches([]); setPublished({}); return; }
    const request = isSaasSession
      ? api.get(saasVideoListPath(brandSlug, routeCampaignId), tenantHeaders(companyId))
      : api.get(`/api/videos/${encodeURIComponent(brandSlug)}`);
    request
      .then((r) => {
        setVideos(r.videos || r || []);
        if (isSaasSession) setPublishBatches(r.publishBatches || []);
      })
      .catch((err) => {
        setVideos([]);
        setError(safeVideoError(err));
      });
    refreshPublished();
  }, [brandSlug, routeCampaignId, refreshPublished, isSaasSession, companyId]);

  const activePublishBatch = useMemo(() => {
    if (!isSaasSession || !funnel?.workflowJobId) return null;
    return publishBatches.find((batch) => batch.workflowJobId === funnel.workflowJobId) || null;
  }, [isSaasSession, funnel?.workflowJobId, publishBatches]);

  async function executeMetaPublish({ workflowJobId, artifactIds, scope, fallbackError }) {
    if (!workflowJobId || !canPublish || publishingScope) return;
    setPublishingScope(scope);
    setPublishError(null);
    try {
      const request = metaPublishExecuteRequest(workflowJobId, artifactIds);
      await api.post(request.path, request.body, tenantHeaders(companyId));
    } catch (err) {
      setPublishError({
        scope,
        workflowJobId,
        message: safeVideoError(err, fallbackError || 'The approved Meta ad batch could not be published. Try again.'),
      });
    } finally {
      // Refresh regardless of outcome — per-ad progress and errors are durable
      // on the intent and only reach the cards through the list payload.
      await refreshPublished();
      setPublishingScope(null);
    }
  }

  function publishApprovedBatch() {
    return executeMetaPublish({
      workflowJobId: activePublishBatch?.workflowJobId,
      scope: 'batch',
    });
  }

  function publishSingleVideo(video) {
    // No fallback workflow target: publishing a video against a different
    // job's intent would reject with misleading not-in-batch advice.
    if (!video?.workflowJobId) return;
    return executeMetaPublish({
      workflowJobId: video.workflowJobId,
      artifactIds: [video.id],
      scope: `video:${video.id}`,
      fallbackError: 'This ad could not be published. Try again.',
    });
  }

  useEffect(() => {
    // Publish errors are scoped to the funnel/job they came from; do not carry
    // them across a funnel switch.
    setPublishError(null);
  }, [funnel?.workflowJobId]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const targetId = routeVideoId
      ? `video-asset-${routeVideoId}`
      : routeSection === 'scripts' ? 'videos-scripts-section'
        : routeSection === 'videos' ? 'videos-gallery-section' : '';
    if (!targetId) return;
    const target = document.getElementById(targetId);
    if (target) target.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [routeSection, routeVideoId, scripts, videos]);

  function toggleScript(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function renderSelected() {
    if (!selected.size || !profileId || !funnel) return;
    setError('');
    const ids = Array.from(selected);
    setRenderState({ active: true, current: '', label: 'Starting…', queueIndex: 0, queueTotal: ids.length });

    for (let i = 0; i < ids.length; i++) {
      const id = ids[i];
      const script = (scripts || []).find((s) => s.id === id);
      if (!script) continue;
      setRenderState({ active: true, current: id, label: 'Starting…', queueIndex: i + 1, queueTotal: ids.length });
      try {
        await streamRender({
          ...(isSaasSession ? { funnelId: funnel.id } : { brandSlug, funnelSlug }),
          scriptId: id,
          profileId,
        }, (label) => setRenderState((prev) => ({ ...prev, active: true, current: id, label })), {
          isSaasSession,
          companyId,
        });
      } catch (err) {
        setError(`Failed on "${script.title}": ${safeVideoError(err, 'Video render failed. Try again.')}`);
        break;
      }
    }
    setRenderState({ active: false, current: '', label: '', queueIndex: 0, queueTotal: 0 });
    setSelected(new Set());
    // Refresh videos.
    refreshPublished();
  }

  if (isSaasSession && (brands === null || profiles === null)) {
    return <ProtectedPageLoader title="Loading videos" body="Checking company access, render profiles, and tenant video artifacts." />;
  }

  return (
    <div className="videos-page" style={{ padding: '32px 40px 64px', maxWidth: 1280, margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>
      <style>{videosResponsiveStyles}</style>
      <header style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
        <h1 style={{
          fontFamily: 'Instrument Serif, serif', fontStyle: 'italic',
          fontSize: 40, fontWeight: 400, margin: 0, color: PALETTE.ink, letterSpacing: '-0.02em',
        }}>Videos</h1>
        <Pill tone="accent">Render</Pill>
      </header>

      {error && (
        <div style={{
          background: PALETTE.dangerSoft, border: '1px solid #E8C9C1', color: '#8B2E1A',
          borderRadius: 12, padding: '12px 16px', fontSize: 13, marginBottom: 20,
        }}>{error}</div>
      )}

      {/* Selectors */}
      <Card style={{ padding: 20, marginBottom: 22 }}>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14,
        }}>
          <SelectField label="Brand" value={brandSlug} onChange={setBrandSlug}
            options={(brands || []).map((b) => ({ value: isSaasSession ? b.id : b.slug, label: b.name }))}
            placeholder={brands == null ? 'Loading…' : 'Pick a brand'}
          />
          <SelectField label="Funnel" value={funnelSlug} onChange={setFunnelSlug}
            options={(funnels || []).map((f) => ({
              value: isSaasSession ? f.id : (f.slug || f.funnelSlug),
              label: funnelOptionLabel(f),
            }))}
            placeholder={!brandSlug ? 'Pick a brand first' : funnels == null ? 'Loading…' : 'Pick a funnel'}
            disabled={!brandSlug}
          />
          <SelectField label="Voice profile" value={profileId} onChange={setProfileId}
            options={(profiles || []).map((p) => ({ value: p.id || p.profileId, label: p.label || p.name || p.id }))}
            placeholder={profiles?.length ? 'Pick a voice' : 'No voices configured'}
          />
        </div>

        {funnel && (!(funnel.campaign?.offer || funnel.offer || funnel._offer) || !(funnel.campaign?.projectType || funnel.projectType || funnel._projectType)) && (
          <div style={{
            marginTop: 14, padding: '12px 14px',
            background: PALETTE.warnSoft, border: '1px solid #EBDCB2', borderRadius: 10,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <span style={{
              width: 22, height: 22, borderRadius: '50%', background: '#FBE3B0', color: PALETTE.warn,
              display: 'grid', placeItems: 'center', fontSize: 13, fontWeight: 700,
            }}>!</span>
            <div style={{ flex: 1, fontSize: 12.5, color: '#7A5A14', lineHeight: 1.5 }}>
              This funnel is missing an offer or project type. Set them on the funnel record so we know which scripts to show.
            </div>
            <button onClick={() => navigate(`funnels?brand=${encodeURIComponent(brandSlug)}&funnel=${encodeURIComponent(funnelSlug)}`)} style={ghostBtn}>
              Open funnel
            </button>
          </div>
        )}

        {(funnel?.campaign?.offer || funnel?.offer || funnel?._offer) && (funnel?.campaign?.projectType || funnel?.projectType || funnel?._projectType) && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
            marginTop: 14, fontSize: 11.5, color: PALETTE.ink3,
            fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.04em', textTransform: 'uppercase',
          }}>
            <span>Showing scripts for:</span>
            <Tag>{OFFER_LABELS[(funnel.campaign?.offer || funnel.offer || funnel._offer)] || (funnel.campaign?.offer || funnel.offer || funnel._offer)}</Tag>
            <span>×</span>
            <Tag>{PROJECT_LABELS[(funnel.campaign?.projectType || funnel.projectType || funnel._projectType)] || (funnel.campaign?.projectType || funnel.projectType || funnel._projectType)}</Tag>
          </div>
        )}
      </Card>

      {/* Scripts */}
      <div id="videos-scripts-section" style={{ scrollMarginTop: 24 }}><SectionTitle n="01">Scripts</SectionTitle></div>
      {!funnel ? (
        <EmptyHint>Pick a brand and a funnel to see matching scripts.</EmptyHint>
      ) : !(funnel.campaign?.offer || funnel.offer || funnel._offer) || !(funnel.campaign?.projectType || funnel.projectType || funnel._projectType) ? (
        <EmptyHint>Assign an offer and project type to this funnel first.</EmptyHint>
      ) : scripts == null ? (
        <EmptyHint>Loading scripts…</EmptyHint>
      ) : scripts.length === 0 ? (
        <EmptyHint>No scripts found for this offer × project type.</EmptyHint>
      ) : (
        <>
          {canRender ? (
            <BulkBar
              count={selected.size}
              total={scripts.length}
              onSelectAll={() => setSelected(new Set(scripts.map((s) => s.id)))}
              onClear={() => setSelected(new Set())}
              onRender={renderSelected}
              renderable={selected.size > 0 && profileId && !renderState.active}
              disabledReason={!profileId ? 'Pick a voice profile' : ''}
              renderState={renderState}
            />
          ) : (
            <EmptyHint>Your company role can review scripts and videos, but cannot start renders.</EmptyHint>
          )}
          <div className="videos-script-grid" style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14,
            marginTop: 14, marginBottom: 32,
          }}>
            {scripts.map((s) => (
              <ScriptCard
                key={s.id}
                script={s}
                selected={selected.has(s.id)}
                rendering={renderState.current === s.id}
                renderLabel={renderState.current === s.id ? renderState.label : ''}
                onToggle={canRender ? () => toggleScript(s.id) : null}
                selectable={canRender}
              />
            ))}
          </div>
        </>
      )}

      {/* Rendered videos */}
      <div id="videos-gallery-section" style={{ scrollMarginTop: 24 }}><SectionTitle n="02">Rendered videos</SectionTitle></div>
      {isSaasSession && activePublishBatch && (
        <CampaignPublishPanel
          batch={activePublishBatch}
          canPublish={canPublish}
          publishing={publishingScope === 'batch'}
          disabled={publishingScope !== null}
          error={publishErrorMessageFor(publishError, 'batch', activePublishBatch.workflowJobId)}
          onPublish={publishApprovedBatch}
        />
      )}
      {!brandSlug ? (
        <EmptyHint>Pick a brand to see its rendered videos.</EmptyHint>
      ) : videos == null ? (
        <EmptyHint>Loading…</EmptyHint>
      ) : videos.length === 0 ? (
        <EmptyHint>No videos rendered yet for this brand.</EmptyHint>
      ) : (
        <div className="videos-gallery-grid" style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14,
        }}>
          {videos.map((v) => (
            <VideoCard
              key={v.id || v.filename || v.name}
              video={v}
              brandSlug={brandSlug}
              published={published[v.filename || v.name]}
              onPublished={refreshPublished}
              isSaasSession={isSaasSession}
              companyId={companyId}
              focused={routeVideoId === v.id}
              canPublish={canPublish}
              publishingScope={publishingScope}
              publishRequestError={publishErrorMessageFor(publishError, `video:${v.id}`)}
              onPublishVideo={publishSingleVideo}
              navigate={navigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── BULK BAR ─────────────────────────────────────────────────────────────
function BulkBar({ count, total, onSelectAll, onClear, onRender, renderable, disabledReason, renderState }) {
  const allSelected = count > 0 && count === total;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      padding: '10px 14px',
      background: PALETTE.surfaceAlt, border: `1px solid ${PALETTE.line}`, borderRadius: 12,
    }}>
      <button onClick={allSelected ? onClear : onSelectAll} style={chipBtn}>
        {allSelected ? 'Clear' : 'Select all'}
      </button>
      <span style={{
        fontSize: 11, fontFamily: 'JetBrains Mono, monospace', color: PALETTE.ink3,
        letterSpacing: '0.06em', textTransform: 'uppercase',
      }}>
        {count} of {total} selected
      </span>
      <div style={{ flex: 1 }} />
      {renderState.active && (
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          fontSize: 12, color: PALETTE.ink2,
        }}>
          <Beacon size="inline" delayMs={0} slowAfterMs={null} />
          {renderState.queueTotal > 1 && (
            <span style={{
              fontFamily: 'JetBrains Mono, monospace', fontSize: 10.5,
              color: PALETTE.ink3, letterSpacing: '0.06em', textTransform: 'uppercase',
            }}>{renderState.queueIndex} / {renderState.queueTotal}</span>
          )}
          <span>{renderState.label}</span>
        </span>
      )}
      {disabledReason && !renderState.active && (
        <span style={{ fontSize: 11.5, color: PALETTE.ink3, fontStyle: 'italic' }}>
          {disabledReason}
        </span>
      )}
      <button onClick={onRender} disabled={!renderable} style={{
        ...inkBtn,
        opacity: renderable ? 1 : 0.45,
        cursor: renderable ? 'pointer' : 'not-allowed',
      }}>
        ▶ Render selected ({count})
      </button>
    </div>
  );
}

// ─── SCRIPT CARD ──────────────────────────────────────────────────────────
function ScriptCard({ script, selected, rendering, renderLabel, onToggle, selectable = true }) {
  const placeholders = script.placeholders || [];
  const compliance = script.compliance || [];
  const body = script.body || script.text || '';
  return (
    <div onClick={rendering || !selectable ? undefined : onToggle} style={{
      cursor: rendering || !selectable ? 'default' : 'pointer', position: 'relative',
      background: rendering ? PALETTE.surface : selected ? PALETTE.accentTint : PALETTE.surface,
      border: `1px solid ${rendering ? PALETTE.accent : selected ? PALETTE.accent : PALETTE.line}`,
      boxShadow: rendering ? `0 0 0 3px ${PALETTE.accentTint}` : selected ? `0 0 0 2px ${PALETTE.accentTint}` : 'none',
      borderRadius: 14, padding: 18,
      display: 'flex', flexDirection: 'column', gap: 10,
      transition: 'all .15s',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <Eyebrow style={{ marginBottom: 6 }}>
            {script.hookType || 'Script'} · {script.estimatedSeconds || '?'}s
          </Eyebrow>
          <div style={{
            fontFamily: 'Instrument Serif, serif', fontSize: 20, fontWeight: 400,
            color: PALETTE.ink, lineHeight: 1.2, letterSpacing: '-0.01em',
          }}>{script.title}</div>
        </div>
        {selectable && <Checkbox checked={selected} />}
      </div>

      <div style={{
        fontSize: 12.5, color: PALETTE.ink2, lineHeight: 1.55,
        display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}>
        {renderBodyWithPlaceholders(body)}
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
        paddingTop: 10, borderTop: `1px solid ${PALETTE.lineSoft}`,
      }}>
        {placeholders.length > 0 && (
          <Tag tone="accent">{placeholders.length} placeholder{placeholders.length === 1 ? '' : 's'}</Tag>
        )}
        {compliance.map((c) => <Tag key={c} tone="warn">{c}</Tag>)}
        {placeholders.length === 0 && compliance.length === 0 && !rendering && (
          <span style={{ fontSize: 11, color: PALETTE.ink3, fontStyle: 'italic' }}>Ready as-is</span>
        )}
        {rendering && (
          <span style={{
            marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 11, color: PALETTE.accentDeep, fontWeight: 500,
          }}>
            <Beacon size="inline" delayMs={0} slowAfterMs={null} />
            {renderLabel || 'Rendering…'}
          </span>
        )}
      </div>
    </div>
  );
}

function renderBodyWithPlaceholders(body) {
  if (!body) return '—';
  // Split on [token] but keep tokens. Highlight them in accent.
  const parts = String(body).split(/(\[[^\]]+\])/g);
  return parts.map((p, i) =>
    /^\[[^\]]+\]$/.test(p)
      ? <span key={i} style={{ color: PALETTE.accent, fontWeight: 500 }}>{p}</span>
      : <span key={i}>{p}</span>
  );
}

export function CampaignPublishPanel({ batch, canPublish, publishing, disabled = false, error, onPublish }) {
  const batchState = saasPublishStatePresentation(batch.state);
  const canExecute = ['ready', 'partially_failed', 'failed'].includes(batch.state);
  const remainingAdCount = Number.isFinite(Number(batch.remainingAdCount))
    ? Number(batch.remainingAdCount)
    : Math.max((batch.approvedAdCount || 0) - (batch.completedAdCount || 0), 0);
  const adErrors = Array.isArray(batch.adErrors) ? batch.adErrors.filter((item) => item?.errorCode || item?.errorMessage) : [];
  const publishLabel = batch.state !== 'ready'
    ? 'Resume approved Meta publish →'
    : batch.completedAdCount > 0
      ? `Publish remaining ad${remainingAdCount === 1 ? '' : 's'} to Meta →`
      : 'Publish approved ads to Meta →';
  const destination = (() => {
    try { return new URL(batch.destination?.url || '').hostname; } catch { return 'Not ready'; }
  })();
  const budget = batch.budget
    ? `${batch.budget.perAdDaily} per ad / day · ${batch.budget.batchDailyMaximum} maximum when activated`
    : 'Uses the approved campaign budget in the connected Meta account currency';
  return (
    <div style={{
      marginBottom: 18, padding: 18, border: `1px solid ${PALETTE.line}`, borderRadius: 8,
      background: PALETTE.surface, display: 'grid', gap: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0, flex: '1 1 420px' }}>
          <Eyebrow style={{ marginBottom: 6 }}>Approved Meta ad batch</Eyebrow>
          <div style={{ fontSize: 18, fontWeight: 600, color: PALETTE.ink, marginBottom: 6 }}>
            {batch.approvedAdCount} approved ad{batch.approvedAdCount === 1 ? '' : 's'}
          </div>
          <div style={{ fontSize: 12.5, color: PALETTE.ink2, lineHeight: 1.55 }}>
            Destination: <strong>{destination}</strong> · Budget basis: {budget}.
            New campaign, ad-set, and ad objects are created paused and cannot spend until separately activated in Meta.
          </div>
        </div>
        <Tag tone={batchState.tone}>{batchState.label}</Tag>
      </div>
      {(batch.publishIntentId || batch.completedAdCount > 0) && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Tag tone="neutral">{batch.completedAdCount}/{batch.approvedAdCount} created</Tag>
          <Tag tone="success">{batch.pausedAdCount} paused</Tag>
          {batch.activeAdCount > 0 && <Tag tone="success">{batch.activeAdCount} active</Tag>}
          {batch.failedAdCount > 0 && <Tag tone="warn">{batch.failedAdCount} incomplete</Tag>}
        </div>
      )}
      {canPublish && canExecute ? (
        <div>
          <button
            onClick={onPublish}
            disabled={publishing || disabled}
            style={{ ...inkBtn, opacity: publishing || disabled ? 0.6 : 1 }}
          >
            {publishing ? 'Publishing approved ads…' : publishLabel}
          </button>
        </div>
      ) : !canPublish ? (
        <div style={{ fontSize: 12.5, color: PALETTE.ink3 }}>Only a company owner or admin can publish the approved batch.</div>
      ) : null}
      {(error || batch.error?.message || adErrors.length > 0) && (
        <div style={{ padding: 10, borderRadius: 8, background: PALETTE.dangerSoft, color: '#7a1a1a', fontSize: 12.5, display: 'grid', gap: 6 }}>
          {error && <div>{error}</div>}
          {!error && batch.error?.message && (
            <div>{batch.error.message}{batch.error.code ? ` (${batch.error.code})` : ''}</div>
          )}
          {adErrors.map((item, index) => (
            <div key={item.artifactId || index} style={{ fontSize: 12 }}>
              <strong>{item.name || 'Approved ad'}</strong>
              {': '}
              {item.errorMessage || 'This ad could not be created in Meta.'}
              {(item.errorCode || item.errorStep) && (
                <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10.5 }}>
                  {' '}({[item.errorCode, item.errorStep].filter(Boolean).join(' · ')})
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── VIDEO CARD ───────────────────────────────────────────────────────────
export function VideoCard({
  video,
  brandSlug,
  published,
  onPublished,
  isSaasSession,
  companyId,
  focused = false,
  canPublish = false,
  publishingScope = null,
  publishRequestError = '',
  onPublishVideo,
  navigate,
}) {
  const filename = video.filename || video.name;
  const [mediaSrc, setMediaSrc] = useState(video.url || '');
  const videoRef = useRef(null);
  const mediaSrcRef = useRef(video.url || '');
  const mediaExpiresAtRef = useRef(0);
  const mediaRequestRef = useRef(null);
  const mediaErrorRetriesRef = useRef(0);

  const refreshMediaUrl = useCallback(async ({ force = false, preserveTime = false, retryPlay = false } = {}) => {
    if (video.url) {
      mediaSrcRef.current = video.url;
      setMediaSrc(video.url);
      return video.url;
    }

    const refreshBeforeMs = 60 * 1000;
    if (!force && mediaSrcRef.current && Date.now() < mediaExpiresAtRef.current - refreshBeforeMs) {
      return mediaSrcRef.current;
    }
    if (mediaRequestRef.current) return mediaRequestRef.current;

    const el = videoRef.current;
    const resumeAt = preserveTime && el ? el.currentTime : 0;
    const shouldRetryPlay = retryPlay || Boolean(el && !el.paused && !el.ended);

    const mediaPath = isSaasSession
      ? `/api/saas/videos/${encodeURIComponent(video.id)}/media`
      : `/api/media/videos/${encodeURIComponent(brandSlug)}/${encodeURIComponent(filename)}/url`;
    mediaRequestRef.current = api.get(mediaPath, isSaasSession ? tenantHeaders(companyId) : undefined)
      .then((result) => {
        const media = isSaasSession ? result.signedUrl : result;
        mediaSrcRef.current = media?.url || '';
        mediaExpiresAtRef.current = media?.expiresAt ? Date.parse(media.expiresAt) : 0;
        setMediaSrc(media?.url || '');
        requestAnimationFrame(() => {
          const current = videoRef.current;
          if (!current) return;
          if (preserveTime && Number.isFinite(resumeAt) && resumeAt > 0) {
            try { current.currentTime = resumeAt; } catch {}
          }
          if (shouldRetryPlay) current.play().catch(() => {});
        });
        return media?.url || '';
      })
      .catch(() => {
        mediaSrcRef.current = '';
        mediaExpiresAtRef.current = 0;
        setMediaSrc('');
        return '';
      })
      .finally(() => {
        mediaRequestRef.current = null;
      });

    return mediaRequestRef.current;
  }, [brandSlug, filename, video.id, video.url, isSaasSession, companyId]);

  useEffect(() => {
    mediaSrcRef.current = video.url || '';
    mediaExpiresAtRef.current = 0;
    setMediaSrc(video.url || '');
    refreshMediaUrl({ force: true }).catch(() => {});
  }, [refreshMediaUrl, video.url]);
  const duration = video.durationSec ? `${Math.round(video.durationSec)}s` : '';
  const publishState = isSaasSession ? video.publish?.state : (published ? 'published' : 'ready');
  const statePresentation = saasPublishStatePresentation(publishState);
  const isPublished = !isSaasSession && publishState === 'published';
  const [publishing, setPublishing] = useState(false);
  const [phase, setPhase] = useState('');
  const [error, setError] = useState(null);

  async function publishToMeta() {
    if (isSaasSession || isPublished || publishing) return;
    setPublishing(true);
    setError(null);
    setPhase('Starting…');
    const token = getToken();
    try {
      const resp = await fetch('/api/meta/publish', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ brandSlug, videos: [{ filename, funnelSlug: video.funnelSlug }] }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let success = false;
      let errEvt = null;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop();
        for (const ln of lines) {
          if (!ln.startsWith('data:')) continue;
          let evt; try { evt = JSON.parse(ln.slice(5).trim()); } catch { continue; }
          if (evt.type === 'phase' || evt.type === 'progress') setPhase(evt.label || evt.phase || '');
          else if (evt.type === 'video-done')                  success = true;
          else if (evt.type === 'done')                        success = true;
          else if (evt.type === 'error')                       errEvt = evt;
        }
      }
      if (errEvt) setError(errEvt);
      else if (success) {
        // Refresh published map + videos list in the parent so this card
        // (and any siblings) re-render with the new ✓ Published state.
        onPublished?.();
      }
      else setError({ error: 'Publish ended without success' });
    } catch (err) {
      setError({ error: safeVideoError(err, 'Publish failed safely. Try again.') });
    } finally {
      setPublishing(false);
      setPhase('');
    }
  }

  return (
    <div id={`video-asset-${video.id}`} style={{
      background: PALETTE.surface, border: `1px solid ${focused ? PALETTE.accent : PALETTE.line}`, borderRadius: 14,
      overflow: 'hidden', display: 'flex', flexDirection: 'column',
      boxShadow: focused ? `0 0 0 3px ${PALETTE.accentTint}` : 'none',
      scrollMarginTop: 24,
    }}>
      <div style={{ background: '#000', aspectRatio: '9/16', maxHeight: 360 }}>
        <ProtectedVideo
          ref={videoRef}
          src={mediaSrc}
          controls
          preload="metadata"
          onPlay={() => { refreshMediaUrl({ preserveTime: true }).catch(() => {}); }}
          onLoadedMetadata={() => { mediaErrorRetriesRef.current = 0; }}
          onError={() => {
            if (mediaErrorRetriesRef.current > 0) return;
            mediaErrorRetriesRef.current += 1;
            refreshMediaUrl({ force: true, preserveTime: true, retryPlay: true }).catch(() => {});
          }}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      </div>
      <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Eyebrow style={{ fontSize: 9.5 }}>{duration || 'Video'}</Eyebrow>
        <div style={{
          fontSize: 13, fontWeight: 500, color: PALETTE.ink, lineHeight: 1.3,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>
          {video.scriptTitle || filename}
        </div>
        <div style={{
          fontSize: 11, color: PALETTE.ink3, fontFamily: 'JetBrains Mono, monospace',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{filename}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
          {isSaasSession ? (
            <>
              <Tag tone={statePresentation.tone}>{statePresentation.label}</Tag>
              {canPublish
                && typeof onPublishVideo === 'function'
                && video.workflowJobId
                && ['ready', 'partially_failed', 'failed'].includes(publishState) && (
                <button
                  onClick={() => onPublishVideo(video)}
                  disabled={publishingScope !== null}
                  style={{ ...inkBtn, padding: '6px 12px', fontSize: 11.5, opacity: publishingScope !== null ? 0.6 : 1 }}
                >
                  {publishingScope === `video:${video.id}` ? <><Beacon size="inline" className="is-current" delayMs={0} /> Publishing…</> : 'Publish to Meta →'}
                </button>
              )}
            </>
          ) : isPublished ? (
            <Tag tone="success">✓ Published</Tag>
          ) : (
            <button onClick={publishToMeta} disabled={publishing} style={{ ...inkBtn, padding: '6px 12px', fontSize: 11.5, opacity: publishing ? 0.6 : 1 }}>
              {publishing ? <><Beacon size="inline" className="is-current" delayMs={0} /> {phase || 'Publishing…'}</> : 'Publish to Meta →'}
            </button>
          )}
        </div>
        {isSaasSession && publishState === 'needs_attention' && (
          <div style={{ fontSize: 11.5, color: PALETTE.ink2, lineHeight: 1.45 }}>
            {safeVideoError(
              { code: video.publish?.errorCode },
              'This video\'s Meta publish state needs attention. Refresh the campaign, then contact support if it persists.',
            )}
          </div>
        )}
        {isSaasSession && publishState === 'not_included' && (
          <div style={{ fontSize: 11.5, color: PALETTE.ink2, lineHeight: 1.45 }}>
            This video was rendered after the campaign batch was approved, so it is not
            part of the approved Meta publish. Request changes and re-approve the
            campaign to include it.
            {video.workflowJobId && typeof navigate === 'function' && (
              <div style={{ marginTop: 6 }}>
                <button
                  onClick={() => navigate(`campaign-review/${video.workflowJobId}`)}
                  style={{ ...chipBtn, padding: '5px 10px', fontSize: 11 }}
                >
                  Open campaign review →
                </button>
              </div>
            )}
          </div>
        )}
        {isSaasSession && publishRequestError && (
          <div style={{
            marginTop: 6, padding: 10, borderRadius: 8,
            background: '#fdecec', border: '1px solid #f5c0c0',
            fontSize: 11.5, color: '#7a1a1a', lineHeight: 1.4,
          }}>
            {publishRequestError}
          </div>
        )}
        {isSaasSession
          && (video.publish?.item?.errorCode || video.publish?.item?.errorMessage)
          && ['ready', 'partially_failed', 'failed'].includes(publishState) && (
          <div style={{
            marginTop: 6, padding: 10, borderRadius: 8,
            background: '#fdecec', border: '1px solid #f5c0c0',
            fontSize: 11.5, color: '#7a1a1a', lineHeight: 1.4,
          }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Last publish attempt failed</div>
            <div>
              {video.publish.item.errorMessage
                || safeVideoError({ code: video.publish.item.errorCode }, 'This ad could not be created in Meta. Try again.')}
            </div>
            {[video.publish.item.errorCode, video.publish.item.errorStep].filter(Boolean).length > 0 && (
              <div style={{ marginTop: 6, fontFamily: 'JetBrains Mono, monospace', fontSize: 10.5, color: '#9a3a3a' }}>
                {[video.publish.item.errorCode, video.publish.item.errorStep].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
        )}
        {error && (
          <div style={{
            marginTop: 6, padding: 10, borderRadius: 8,
            background: '#fdecec', border: '1px solid #f5c0c0',
            fontSize: 11.5, color: '#7a1a1a', lineHeight: 1.4,
          }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              {error.userTitle || 'Publish failed'}
            </div>
            <div>{error.userMessage || error.message || error.error}</div>
            {(error.code != null || error.subcode != null || error.fbtraceId) && (
              <div style={{ marginTop: 6, fontFamily: 'JetBrains Mono, monospace', fontSize: 10.5, color: '#9a3a3a' }}>
                {error.code != null && `code ${error.code}`}
                {error.subcode != null && ` · subcode ${error.subcode}`}
                {error.fbtraceId && ` · trace ${error.fbtraceId}`}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── STREAMING RENDER ─────────────────────────────────────────────────────
// Pretty labels for backend phase keys. Falls back to the raw label if the
// backend hands us one we don't recognize.
const PHASE_LABELS = {
  spawn:        'Starting render pipeline',
  synth:        'Synthesizing voice',
  transcribe:   'Transcribing for word timing',
  fetch_broll:  'Fetching b-roll clips',
  compose:      'Composing video',
  burn:         'Burning subtitles',
  upload:       'Uploading to Cloudinary',
  save:         'Saving to brand library',
};

async function streamRender(payload, onProgress, { isSaasSession = false, companyId = '' } = {}) {
  const token = getToken();
  const res = await fetch(isSaasSession ? '/api/saas/videos/render' : '/api/video/render', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(isSaasSession ? {
        'x-company-id': companyId,
        'Idempotency-Key': `video:${payload.funnelId}:${payload.scriptId}:${Date.now()}:${Math.random().toString(36).slice(2)}`,
      } : {}),
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = new Error('Video render request failed');
    error.status = res.status;
    error.code = body.error;
    throw error;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf('\n\n')) !== -1) {
      const frame = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      const line = frame.split('\n').find((l) => l.startsWith('data:'));
      if (!line) continue;
      let evt;
      try { evt = JSON.parse(line.slice(5).trim()); } catch { continue; }
      if (evt.type === 'phase') {
        const label = PHASE_LABELS[evt.phase] || evt.label || 'Working…';
        onProgress?.(label);
      } else if (evt.type === 'progress' && evt.label) {
        onProgress?.(evt.label);
      } else if (evt.type === 'error') {
        const error = new Error(evt.message || 'Video render failed');
        error.code = evt.error || 'video_render_failed';
        throw error;
      }
    }
  }
}

// ─── ATOMS ────────────────────────────────────────────────────────────────
function SelectField({ label, value, onChange, options, placeholder, disabled }) {
  return (
    <div>
      <Eyebrow style={{ fontSize: 9.5, color: PALETTE.ink3, marginBottom: 6 }}>{label}</Eyebrow>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        style={{
          width: '100%', padding: '10px 12px', fontSize: 13,
          fontFamily: 'Inter, sans-serif', color: value ? PALETTE.ink : PALETTE.ink3,
          background: disabled ? PALETTE.surfaceAlt : PALETTE.surface,
          border: `1px solid ${PALETTE.line}`, borderRadius: 9, outline: 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <option value="">{placeholder || '—'}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

function SectionTitle({ n, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginTop: 24, marginBottom: 14 }}>
      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: PALETTE.ink3, letterSpacing: '0.1em' }}>
        {n}
      </span>
      <h2 style={{
        margin: 0, fontFamily: 'Instrument Serif, serif',
        fontSize: 28, fontWeight: 400, color: PALETTE.ink, letterSpacing: '-0.01em',
      }}>{children}</h2>
    </div>
  );
}

function EmptyHint({ children }) {
  return (
    <div style={{
      padding: '20px 24px',
      background: PALETTE.surfaceAlt, border: `1px dashed ${PALETTE.line}`, borderRadius: 12,
      color: PALETTE.ink3, fontSize: 13, fontStyle: 'italic',
      marginBottom: 14,
    }}>{children}</div>
  );
}

function Eyebrow({ children, style = {} }) {
  return (
    <div style={{
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: 10.5, letterSpacing: '0.14em', textTransform: 'uppercase',
      color: PALETTE.accent, fontWeight: 500,
      ...style,
    }}>{children}</div>
  );
}

function Tag({ children, tone = 'neutral' }) {
  const tones = {
    neutral: { bg: PALETTE.surfaceAlt, fg: PALETTE.ink2, bd: PALETTE.line },
    accent:  { bg: PALETTE.accentTint, fg: PALETTE.accentDeep, bd: PALETTE.accentSoft },
    warn:    { bg: PALETTE.warnSoft, fg: PALETTE.warn, bd: '#EBDCB2' },
    success: { bg: PALETTE.successSoft, fg: PALETTE.success, bd: '#CFD9B4' },
  };
  const t = tones[tone];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', fontSize: 10.5,
      background: t.bg, color: t.fg, border: `1px solid ${t.bd}`,
      borderRadius: 999, fontFamily: 'Inter, sans-serif',
      fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase',
    }}>{children}</span>
  );
}

function Checkbox({ checked }) {
  return (
    <span style={{
      width: 18, height: 18, borderRadius: 5,
      border: `1.5px solid ${checked ? PALETTE.accent : PALETTE.line}`,
      background: checked ? PALETTE.accent : 'transparent',
      display: 'grid', placeItems: 'center', flexShrink: 0,
      color: '#fff', fontSize: 11, fontWeight: 900,
    }}>{checked ? '✓' : ''}</span>
  );
}

const inkBtn = {
  padding: '8px 14px', fontSize: 12.5, fontWeight: 500,
  background: PALETTE.ink, color: '#FAF8F5',
  border: `1px solid ${PALETTE.ink}`, borderRadius: 9, cursor: 'pointer',
  fontFamily: 'Inter, sans-serif',
};
const ghostBtn = {
  padding: '8px 12px', fontSize: 12, fontWeight: 500,
  background: 'transparent', color: PALETTE.ink,
  border: `1px solid ${PALETTE.line}`, borderRadius: 9, cursor: 'pointer',
  fontFamily: 'Inter, sans-serif',
};
const chipBtn = {
  padding: '6px 12px', fontSize: 12, fontWeight: 500, borderRadius: 999,
  background: 'transparent', color: PALETTE.ink2,
  border: `1px solid ${PALETTE.line}`, cursor: 'pointer',
  fontFamily: 'Inter, sans-serif',
};
